import { useId } from "react";

import Icon from "@/components/icons";
import { cn } from "@/lib/utils";

import ProductSearchListItem from "./ProductSearchListItem";
import { ProductSearchListItemProps } from "./ProductSearchListItem";
import { ElementType } from "@/constants/tagular/main";
import EventLink from "@/components/Link/EventLink/EventLink";
import type { Category } from "../../hooks/useAlgoliaAutocomplete";
import useTranslations from "@/i18n/useTranslations";
import { useLocale } from "next-intl";
import { withLocalePrefix } from "@/i18n/withLocalePrefix";

export type { Category };

type AfterStateProps = {
  categories?: Category[];
  resultItems: ProductSearchListItemProps[];
  query: string;
  location?: string;
  maxProducts?: number;
  maxCategories?: number;
};

function escapeRegExp(fragment: string) {
  return fragment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** P4: 18px / 700 / 140%; match = primary-500 bold, remainder slightly muted. */
function FacetQueryLabel({ name, query }: { name: string; query: string }) {
  const root = "font-sans text-[18px] leading-[1.4] line-clamp-2 min-w-0";
  const trimmed = query.trim();
  if (!trimmed) {
    return (
      <span className={cn(root, "font-bold text-primary-500")}>{name}</span>
    );
  }

  const match = new RegExp(`(${escapeRegExp(trimmed)})`, "i").exec(name);
  if (!match || match.index === undefined) {
    return (
      <span className={cn(root, "font-bold text-primary-500")}>{name}</span>
    );
  }

  const start = match.index;
  const end = start + match[1].length;

  return (
    <span className={root}>
      {start > 0 ? (
        <span className="font-normal text-primary-500/75">
          {name.slice(0, start)}
        </span>
      ) : null}
      <strong className="font-bold text-primary-500">
        {name.slice(start, end)}
      </strong>
      {end < name.length ? (
        <span className="font-normal text-primary-500/75">
          {name.slice(end)}
        </span>
      ) : null}
    </span>
  );
}

const AfterState = ({
  categories,
  resultItems,
  query,
  location = "embedded-search",
  maxProducts = 2,
  maxCategories = 7,
}: AfterStateProps) => {
  const t = useTranslations("Common");
  const tSearch = useTranslations("Search");
  const locale = useLocale();
  const instanceId = useId();
  const facetsHeadingId = `${instanceId}-facet-suggestions-heading`;
  const coursesHeadingId = `${instanceId}-course-suggestions-heading`;

  const categoriesToShow = categories?.slice(0, maxCategories) || [];
  const productsToShow = resultItems.slice(0, Math.max(0, maxProducts)) || [];

  return (
    <div
      role="region"
      aria-label={t("searchSuggestions")}
      className="w-full flex flex-col"
    >
      {productsToShow.length > 0 && (
        <section
          className={cn(
            "pb-6",
            categoriesToShow.length > 0 && "border-b border-putty-600",
          )}
          aria-labelledby={coursesHeadingId}
        >
          <span id={coursesHeadingId} className="sr-only">
            {tSearch("courseSuggestions")}
          </span>
          <ul className="m-0 grid list-none grid-cols-1 gap-3 !p-0 md:grid-cols-2">
            {productsToShow.map((item, index) => (
              <li key={item.id} className="m-0 min-w-0">
                <div className="px-0 py-0">
                  <ProductSearchListItem
                    id={item.id}
                    productName={item.productName}
                    productTitleHtml={item.productTitleHtml}
                    productCategory={item.productCategory}
                    partner={item.partner}
                    partnerLogoUrl={item.partnerLogoUrl}
                    variant={item.variant}
                    analyticsDurationDisplay={item.analyticsDurationDisplay}
                    analyticsLevel={item.analyticsLevel}
                    analyticsFlexibility={item.analyticsFlexibility}
                    analyticsSkillsDisplay={item.analyticsSkillsDisplay}
                    viewedSince={item.viewedSince}
                    timeToComplete={item.timeToComplete}
                    href={withLocalePrefix(item.href, locale)}
                    index={index}
                    location={location}
                    listingContext="after-query"
                    compact
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {categoriesToShow.length > 0 && (
        <section
          className={cn(productsToShow.length > 0 ? "pt-6" : "")}
          aria-labelledby={facetsHeadingId}
        >
          <span id={facetsHeadingId} className="sr-only">
            {tSearch("suggestedSearchTerms")}
          </span>
          <ul role="list" className="m-0 list-none !p-0">
            {categoriesToShow.map((item, index) => (
              <li key={`${item.categoryUrl}:${item.name}`} className="m-0">
                <EventLink
                  href={withLocalePrefix(item.categoryUrl, locale)}
                  element_type={ElementType.Entry}
                  html_id="search-bar-results"
                  location={location}
                  text={item.name}
                  stickyElementView
                  visible_text={
                    <span className="flex items-center gap-3 min-w-0">
                      <Icon
                        type="magnifying-glass"
                        svgProps={{
                          width: 20,
                          height: 20,
                          className: "shrink-0 text-primary-500",
                          "aria-hidden": true,
                        }}
                        pathProps={{ fill: "currentColor" }}
                      />
                      <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2 gap-y-1">
                        <FacetQueryLabel name={item.name} query={query} />
                        {item.count > 0 ? (
                          <span className="font-normal text-primary-500/75 font-sans text-sm shrink-0">
                            ({item.count})
                          </span>
                        ) : null}
                      </span>
                    </span>
                  }
                  name={item.name}
                  position={(index + 1).toString()}
                  class_name="flex w-full min-h-[52px] items-center gap-3 px-0 py-4 outline-none transition-colors hover:bg-putty-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AfterState;
